/**
 * จัดการผู้ใช้สำหรับผู้ดูแลระบบ
 *
 * ระบบไม่มีการส่งอีเมล การตั้งรหัสผ่านจึงเป็นกระบวนการที่ผู้ดูแลทำให้
 * แล้วแจ้งรหัสให้ผู้ใช้ทางโทรศัพท์หรือ LINE
 * หน้าจอนี้จึงต้องแสดงรหัสชั่วคราวให้ชัดและคัดลอกง่าย
 */
import { useCallback, useEffect, useState } from 'react';
import { api, apiFetch, ApiError } from '../lib/api';
import { ROLE_LABELS_TH, ROLES, type Role } from '../../shared/types';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  areaCode: string | null;
  active: boolean;
}

interface SeatInfo {
  activeCount: number;
  maxActiveUsers: number;
  freeSeatLimit: number;
  remaining: number;
  nearingLimit: boolean;
  exceedsFreeTier: boolean;
}

export function AdminUsersRoute() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [seats, setSeats] = useState<SeatInfo | null>(null);
  const [withoutPassword, setWithoutPassword] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** รหัสชั่วคราวที่เพิ่งสร้าง แสดงครั้งเดียว เปิดดูซ้ำไม่ได้ */
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const [form, setForm] = useState({
    email: '',
    displayName: '',
    role: 'field_officer' as Role,
    areaCode: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [page, seatData, pending] = await Promise.all([
        apiFetch<{ items: UserRow[] }>('/api/admin/users?limit=100'),
        apiFetch<SeatInfo>('/api/admin/users/seats'),
        apiFetch<{ items: { id: string }[] }>('/api/admin/users/without-password'),
      ]);
      setUsers(page.items);
      setSeats(seatData);
      setWithoutPassword(new Set(pending.items.map((item) => item.id)));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'โหลดข้อมูลผู้ใช้ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    /*
     * eslint-disable-next-line react-hooks/set-state-in-effect --
     * เหตุผลเดียวกับใน Search.tsx: นี่คือการโหลดข้อมูลตอน mount ซึ่งต้องอยู่ใน effect
     * setState ทุกจุดใน load() เกิดหลัง await ทั้งหมด ไม่ใช่ synchronous
     * แต่ rule ตรวจแบบ transitive จึงมองไม่เห็นความต่างนี้
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  const createUser = () =>
    run('new', async () => {
      const created = await apiFetch<{ user: UserRow }>('/api/admin/users', {
        method: 'POST',
        body: {
          email: form.email.trim().toLowerCase(),
          displayName: form.displayName.trim(),
          role: form.role,
          areaCode: form.areaCode.trim() || null,
        },
      });
      // สร้างเสร็จแล้วออกรหัสชั่วคราวให้ทันที ไม่งั้นผู้ใช้ยังเข้าระบบไม่ได้
      const reset = await api.resetUserPassword(created.user.id);
      setIssued({ email: created.user.email, password: reset.temporaryPassword });
      setForm({ email: '', displayName: '', role: 'field_officer', areaCode: '' });
    });

  const resetPassword = (user: UserRow) =>
    run(user.id, async () => {
      const reset = await api.resetUserPassword(user.id);
      setIssued({ email: user.email, password: reset.temporaryPassword });
    });

  const revokeSessions = (user: UserRow) =>
    run(user.id, async () => {
      const result = await api.revokeUserSessions(user.id);
      setError(null);
      setIssued(null);
      window.alert(`ตัดการเข้าใช้งานแล้ว ${result.revokedSessions} เครื่อง`);
    });

  const toggleActive = (user: UserRow) =>
    run(user.id, async () => {
      await apiFetch(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: { active: !user.active },
      });
    });

  if (loading) {
    return (
      <div className="empty">
        <span className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  return (
    <div className="stack-lg">
      {seats && (
        <section className="card stack">
          <h2>โควตาบัญชี</h2>
          <p>
            <b className="mono" style={{ fontSize: 'var(--text-xl, 1.3rem)' }}>
              {seats.activeCount} / {seats.maxActiveUsers}
            </b>{' '}
            บัญชีที่ใช้งานอยู่ · เหลืออีก {seats.remaining}
          </p>
          {seats.exceedsFreeTier ? (
            <div className="banner banner-bad" role="alert">
              <span>
                เกินโควตาฟรี {seats.freeSeatLimit} บัญชีแล้ว จะมีค่าใช้จ่ายรายหัวกับทุกบัญชี
              </span>
            </div>
          ) : seats.nearingLimit ? (
            <div className="banner banner-warn">
              <span>
                ใกล้เพดานแล้ว หากต้องเกิน {seats.freeSeatLimit} บัญชี ต้องยืนยันงบประมาณก่อน
              </span>
            </div>
          ) : null}
        </section>
      )}

      {error && (
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
      )}

      {issued && (
        <section className="card stack">
          <h2>รหัสชั่วคราว</h2>
          <div className="banner banner-warn">
            <span>
              รหัสนี้แสดงครั้งเดียว เปิดดูซ้ำไม่ได้ ถ้าลืมต้องตั้งใหม่
              แจ้งให้ผู้ใช้ทางช่องทางที่ปลอดภัย
            </span>
          </div>
          <p className="small muted">สำหรับ {issued.email}</p>
          <p
            className="mono"
            style={{ fontSize: '1.6rem', letterSpacing: '0.12em', userSelect: 'all' }}
          >
            {issued.password}
          </p>
          <div className="row">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void navigator.clipboard?.writeText(issued.password)}
            >
              คัดลอก
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setIssued(null)}>
              ปิด
            </button>
          </div>
          <p className="small muted">ระบบจะบังคับให้ผู้ใช้ตั้งรหัสของตัวเองตอนเข้าครั้งแรก</p>
        </section>
      )}

      <section className="card stack">
        <h2>เพิ่มผู้ใช้</h2>
        <div className="field">
          <label className="label" htmlFor="u-email">
            ชื่อผู้ใช้
          </label>
          <input
            id="u-email"
            className="input"
            type="text"
            value={form.email}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="u-name">
            ชื่อที่แสดง
          </label>
          <input
            id="u-name"
            className="input"
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="u-role">
            บทบาท
          </label>
          <select
            id="u-role"
            className="input"
            value={form.role}
            onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS_TH[role]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="u-area">
            รหัสพื้นที่
          </label>
          <input
            id="u-area"
            className="input mono"
            value={form.areaCode}
            placeholder="เว้นว่าง = ทุกพื้นที่"
            onChange={(event) => setForm({ ...form, areaCode: event.target.value.toUpperCase() })}
          />
        </div>
        <button
          type="button"
          className="btn btn-block"
          disabled={busyId !== null || !form.email.trim() || !form.displayName.trim()}
          onClick={() => void createUser()}
        >
          เพิ่มผู้ใช้และออกรหัสชั่วคราว
        </button>
      </section>

      <section className="stack">
        <h2>ผู้ใช้ทั้งหมด ({users.length})</h2>
        <div className="list">
          {users.map((user) => (
            <div key={user.id} className="list-row" style={{ flexWrap: 'wrap' }}>
              <div className="grow">
                <b>{user.displayName}</b>
                <p className="small muted">
                  {user.email} · {ROLE_LABELS_TH[user.role]} · {user.areaCode ?? 'ทุกพื้นที่'}
                </p>
                <div className="row" style={{ gap: 'var(--space-2)', marginTop: '4px' }}>
                  <span className={`pill pill-${user.active ? 'ok' : 'muted'}`}>
                    {user.active ? 'ใช้งานอยู่' : 'ระงับ'}
                  </span>
                  {withoutPassword.has(user.id) && (
                    <span className="pill pill-warn">ยังไม่ได้ตั้งรหัสผ่าน</span>
                  )}
                </div>
              </div>
              <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busyId !== null}
                  onClick={() => void resetPassword(user)}
                >
                  ตั้งรหัสใหม่
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busyId !== null}
                  title="ใช้เมื่อผู้ใช้แจ้งว่ามือถือหาย"
                  onClick={() => void revokeSessions(user)}
                >
                  ตัดทุกเครื่อง
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busyId !== null}
                  onClick={() => void toggleActive(user)}
                >
                  {user.active ? 'ระงับ' : 'เปิดใช้งาน'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
